/**
 * Sign-in method change (email / phone) — platform SDK repair shim.
 *
 * WHY THIS EXISTS
 * ---------------
 * `@mindstudio-ai/interface@0.1.20` (the latest published) has a broken
 * change-a-sign-in-method flow:
 *
 *   requestPhoneChange(newPhone) -> POST /_/auth/phone/change { newPhone }
 *        (the response carries a `verificationId`, which the SDK discards)
 *   confirmPhoneChange(newPhone, code) -> POST /_/auth/phone/change/confirm
 *                                          { newPhone, code }
 *
 * But the current platform backend requires the CONFIRM step to send the
 * `verificationId` from the send step, not the new phone/email:
 *
 *   POST /_/auth/phone/change/confirm -> 400
 *     { errorString: "missing_params",
 *       errorMessage: "verificationId and code are required." }
 *
 * So confirm ALWAYS fails and no member can add or change a sign-in method.
 * (Reproduced end-to-end: send returns 200, confirm returns 400.) The email
 * change pair is broken the same way.
 *
 * WHY A FETCH SHIM (and not our own fetch)
 * ----------------------------------------
 * The authenticated session lives in the SDK's in-memory Bearer token, which it
 * does not expose. A hand-rolled same-origin fetch has no way to present it and
 * is rejected (`dev_session_expired` / 401). So instead of replacing the SDK
 * calls, we let the SDK make its normal, correctly-authenticated requests and
 * only repair them in flight:
 *   - capture the `verificationId` from each send response, and
 *   - inject it into the matching confirm request body.
 * The SDK keeps owning auth AND session/user state (its confirm response is
 * still processed normally), so `onAuthStateChanged` and the app store update
 * exactly as they would if the SDK were correct — no reload, no state hacks.
 *
 * Scope: the shim touches ONLY the four auth-change endpoints and passes every
 * other request through untouched. Installed once at app startup.
 *
 * WHEN TO DELETE
 * --------------
 * When a fixed `@mindstudio-ai/interface` ships, delete this file and the single
 * `installAuthChangeFix()` call at the app entry. Nothing else depends on it.
 */

/** Latest verificationId captured per change kind, keyed by endpoint family. */
const lastVerificationId: Record<'email' | 'phone', string | null> = {
  email: null,
  phone: null,
};

function changeKind(url: string): 'email' | 'phone' | null {
  if (url.includes('/_/auth/phone/change')) return 'phone';
  if (url.includes('/_/auth/email/change')) return 'email';
  return null;
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  if (input instanceof Request) return input.url;
  return String(input);
}

/**
 * If a confirm response is a leaked duplicate-key / unique-constraint error
 * (the new email/phone already belongs to another account), return a clean 409
 * Response the SDK and call sites understand. Returns null for anything else, so
 * genuine server errors pass through unchanged.
 */
async function rewriteDuplicateError(res: Response, kind: 'email' | 'phone'): Promise<Response | null> {
  let raw = '';
  try {
    raw = await res.clone().text();
  } catch {
    return null;
  }
  const isDuplicate =
    /duplicate key|unique constraint|app_phone|app_email|already .*(in use|linked|registered|taken)/i.test(raw);
  if (!isDuplicate) return null;

  const message =
    kind === 'phone'
      ? 'This number is already linked to another account.'
      : 'This email is already linked to another account.';
  return new Response(
    JSON.stringify({ error: message, code: kind === 'phone' ? 'phone_in_use' : 'email_in_use' }),
    { status: 409, headers: { 'Content-Type': 'application/json' } },
  );
}

/**
 * Install the repair shim over `window.fetch`. Idempotent and a no-op outside
 * the browser. Call once, before any auth-change flow can run.
 */
export function installAuthChangeFix(): void {
  if (typeof window === 'undefined') return;
  const w = window as typeof window & { __ms_authChangeFixed?: boolean };
  if (w.__ms_authChangeFixed) return;
  w.__ms_authChangeFixed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = urlOf(input);
    const kind = changeKind(url);

    // Not an auth-change request — leave it completely untouched.
    if (!kind) return originalFetch(input, init);

    const isConfirm = url.includes('/change/confirm');

    // CONFIRM: rewrite the body so it carries the verificationId the backend
    // requires (the SDK sends { newPhone|newEmail, code } and omits it). Only
    // rewrite when the SDK passed a string body via init (the shape it uses);
    // anything else falls through unchanged.
    if (isConfirm) {
      if (init && typeof init.body === 'string') {
        const vid = lastVerificationId[kind];
        if (vid) {
          try {
            const body = JSON.parse(init.body);
            body.verificationId = vid;
            init = { ...init, body: JSON.stringify(body) };
          } catch {
            /* non-JSON body — leave as-is */
          }
        }
      }
      const res = await originalFetch(input, init);
      // The platform leaks a raw DB unique-constraint error as a 500 when the
      // new email/phone already belongs to another account, and the SDK's error
      // parser only reads an `error` field — so that case would otherwise surface
      // to the user as a generic "could not reach the server". Detect it and
      // rewrite the response into a clean 409 the call sites already handle.
      if (!res.ok) {
        const rewritten = await rewriteDuplicateError(res, kind);
        if (rewritten) return rewritten;
      }
      return res;
    }

    // SEND: let the request go, then capture the verificationId from a copy of
    // the response (the original stream is untouched for the SDK to read).
    const res = await originalFetch(input, init);
    if (res.ok) {
      try {
        const data = await res.clone().json();
        if (data && typeof data.verificationId === 'string') {
          lastVerificationId[kind] = data.verificationId;
        }
      } catch {
        /* no JSON / no verificationId — nothing to capture */
      }
    }
    return res;
  };
}
